import { DEFAULT_DQF_CHECKLIST_ITEMS, InspectionAttachmentSummary } from '@numeo/types-core'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useCallback } from 'react'
import {
    AccidentFilterParams,
    getSafetyComplianceAlerts as apiGetSafetyComplianceAlerts,
    restoreDOTInspection as apiRestoreDOTInspection,
    voidDOTInspection as apiVoidDOTInspection,
    DOTInspectionFilterParams,
    getAccidents,
    getApiErrorMessage,
    getCompanyFiles,
    getDOTInspections,
    getDqfChecklistConfig,
    getDrugTests,
    getIFTAReports,
    getSafetyDashboard,
    getSafetyDrivers,
    getSafetyTrucks,
    type SafetyDriver,
} from 'services/safetyApi'
import {
    selectAccidents,
    selectCompanyFiles,
    selectDOTInspections,
    selectDqfChecklistConfig,
    selectDrugTests,
    selectIFTAReports,
    selectSafetyComplianceAlerts,
    selectSafetyDashboard,
    selectSafetyDrivers,
    selectSafetyTrucks,
    setAccidents,
    setAccidentsError,
    setAccidentsLoading,
    setCompanyFiles,
    setCompanyFilesError,
    setCompanyFilesLoading,
    setDOTInspections,
    setDOTInspectionsError,
    setDOTInspectionsLoading,
    updateDOTInspectionAttachments as patchDOTInspectionAttachments,
    setDqfChecklistConfig,
    setDqfChecklistConfigLoading,
    setDrivers,
    setDriversError,
    setDriversLoading,
    setDrugTests,
    setDrugTestsError,
    setDrugTestsLoading,
    setIFTAReports,
    setIFTAReportsError,
    setIFTAReportsLoading,
    setSafetyAlerts,
    setSafetyAlertsError,
    setSafetyAlertsLoading,
    setSafetyDashboardData,
    setSafetyDashboardError,
    setSafetyDashboardLoading,
    setTrucks,
    setTrucksError,
    setTrucksLoading,
} from 'store/safetySlice'
export const useSafetyData = () => {
    const dispatch = useAppDispatch()
    const dashboard = useAppSelector(selectSafetyDashboard)
    const drivers = useAppSelector(selectSafetyDrivers)
    const trucks = useAppSelector(selectSafetyTrucks)
    const dotInspections = useAppSelector(selectDOTInspections)
    const accidents = useAppSelector(selectAccidents)
    const drugTests = useAppSelector(selectDrugTests)
    const iftaReports = useAppSelector(selectIFTAReports)
    const companyFiles = useAppSelector(selectCompanyFiles)
    const complianceAlerts = useAppSelector(selectSafetyComplianceAlerts)
    const dqfChecklistConfig = useAppSelector(selectDqfChecklistConfig)

    const fetchDashboard = useCallback(async () => {
        dispatch(setSafetyDashboardLoading(true))
        try {
            const data = await getSafetyDashboard()
            dispatch(setSafetyDashboardData(data))
        } catch (error: unknown) {
            dispatch(setSafetyDashboardError(getApiErrorMessage(error, 'Failed to fetch safety dashboard')))
        }
    }, [dispatch])

    /**
     * A silent refresh leaves the page-level loading flag alone, so a scoped save — the DQF
     * checklist, say — can show its own spinner instead of blanking the whole page. Returns
     * the drivers so a caller can pick the one it just edited out of fresh server state.
     */
    const fetchDrivers = useCallback(
        async (options?: { silent?: boolean }): Promise<SafetyDriver[] | null> => {
            if (!options?.silent) dispatch(setDriversLoading(true))
            try {
                const data = await getSafetyDrivers()
                dispatch(setDrivers(data))
                return data
            } catch (error: unknown) {
                dispatch(setDriversError(getApiErrorMessage(error, 'Failed to fetch drivers')))
                return null
            }
        },
        [dispatch]
    )

    const fetchTrucks = useCallback(async () => {
        dispatch(setTrucksLoading(true))
        try {
            const data = await getSafetyTrucks()
            dispatch(setTrucks(data))
        } catch (error: unknown) {
            dispatch(setTrucksError(getApiErrorMessage(error, 'Failed to fetch trucks')))
        }
    }, [dispatch])

    const fetchDOTInspections = useCallback(
        async (filters?: DOTInspectionFilterParams, options?: { silent?: boolean }) => {
            if (!options?.silent) dispatch(setDOTInspectionsLoading(true))
            try {
                const data = await getDOTInspections(filters)
                dispatch(setDOTInspections(data))
            } catch (error: unknown) {
                dispatch(setDOTInspectionsError(getApiErrorMessage(error, 'Failed to fetch DOT inspections')))
            }
        },
        [dispatch]
    )

    const voidDOTInspection = useCallback(
        async (id: string, reason: string) => {
            await apiVoidDOTInspection(id, reason)
            await fetchDOTInspections()
        },
        [fetchDOTInspections]
    )

    const restoreDOTInspection = useCallback(
        async (id: string) => {
            await apiRestoreDOTInspection(id)
            await fetchDOTInspections()
        },
        [fetchDOTInspections]
    )

    const updateDOTInspectionAttachments = useCallback(
        (id: string, attachments: InspectionAttachmentSummary[]) => dispatch(patchDOTInspectionAttachments({ id, attachments })),
        [dispatch]
    )

    const fetchAccidents = useCallback(
        async (filters?: AccidentFilterParams) => {
            dispatch(setAccidentsLoading(true))
            try {
                const data = await getAccidents(filters)
                dispatch(setAccidents(data))
            } catch (error: unknown) {
                dispatch(setAccidentsError(getApiErrorMessage(error, 'Failed to fetch accidents')))
            }
        },
        [dispatch]
    )

    const fetchDrugTests = useCallback(async () => {
        dispatch(setDrugTestsLoading(true))
        try {
            const data = await getDrugTests()
            dispatch(setDrugTests(data))
        } catch (error: unknown) {
            dispatch(setDrugTestsError(getApiErrorMessage(error, 'Failed to fetch drug tests')))
        }
    }, [dispatch])

    const fetchIFTAReports = useCallback(async () => {
        dispatch(setIFTAReportsLoading(true))
        try {
            const data = await getIFTAReports({ limit: 200 })
            dispatch(setIFTAReports(data))
        } catch (error: unknown) {
            dispatch(setIFTAReportsError(getApiErrorMessage(error, 'Failed to fetch IFTA reports')))
        }
    }, [dispatch])

    const fetchCompanyFiles = useCallback(async () => {
        dispatch(setCompanyFilesLoading(true))
        try {
            const data = await getCompanyFiles()
            dispatch(setCompanyFiles(data))
        } catch (error: unknown) {
            dispatch(setCompanyFilesError(getApiErrorMessage(error, 'Failed to fetch company files')))
        }
    }, [dispatch])

    const fetchComplianceAlerts = useCallback(async () => {
        dispatch(setSafetyAlertsLoading(true))
        try {
            const data = await apiGetSafetyComplianceAlerts()
            dispatch(setSafetyAlerts(data))
        } catch (error: unknown) {
            dispatch(setSafetyAlertsError(getApiErrorMessage(error, 'Failed to fetch compliance alerts')))
        }
    }, [dispatch])

    const fetchDqfChecklistConfig = useCallback(async () => {
        dispatch(setDqfChecklistConfigLoading(true))
        try {
            const data = await getDqfChecklistConfig()
            dispatch(setDqfChecklistConfig(data))
        } catch {
            // Fall back to the default checklist so the page doesn't silently render an empty table —
            // mirrors the server's own fallback in safety/utils/dqf-checklist.ts's getDqfChecklistConfig.
            // (setDqfChecklistConfig itself clears any error state, so there's no separate error to report here.)
            dispatch(setDqfChecklistConfig(DEFAULT_DQF_CHECKLIST_ITEMS))
        }
    }, [dispatch])

    return {
        dashboard,
        drivers,
        trucks,
        dotInspections,
        accidents,
        drugTests,
        iftaReports,
        companyFiles,
        complianceAlerts,
        dqfChecklistConfig,
        fetchDashboard,
        fetchDrivers,
        fetchTrucks,
        fetchDOTInspections,
        voidDOTInspection,
        restoreDOTInspection,
        updateDOTInspectionAttachments,
        fetchAccidents,
        fetchDrugTests,
        fetchIFTAReports,
        fetchCompanyFiles,
        fetchComplianceAlerts,
        fetchDqfChecklistConfig,
    }
}
